import { SizingTypeValue } from "../../types/enums";
import {
  calculateCurrentSelectedInterestRate,
  cleanedAdressString,
  sanitizePercentage,
} from "../../utils/Common";
import { prePayTypeToMonthsMap } from "../../utils/v2/common";
const getBridgeLoanType = (fetchData: any, propertyDetails: any) => {
  if (
    propertyDetails?.[0]?.propertyInformation?.propertyType === "Multifamily" &&
    ["Stabilized", "Fix & Flip"].includes(fetchData?.loanType)
  ) {
    return "BridgeLoanMultifamily";
  } else if (fetchData?.loanType === "Ground Up") {
    return "BridgeLoanGroundUp";
  } else {
    return "BridgeLoan24";
  }
};

const sanitizeCurrency = (value: any, toFixed?: number) => {
  if (isNaN(Number(value))) {
    return value;
  }
  const data = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: toFixed || 2,
  }).format(value);

  return data;
};

const getSelectedBuyDownOptions = (
  loanData: any,
  loanResultData: any,
  currentSelectedInterestRate: any
) => {
  let buyDownOptions = loanData?.buyDownOptions;
  let selectedBuyDownOptions = findSelectedBuyDownOptions(
    buyDownOptions,
    currentSelectedInterestRate
  );
  if (selectedBuyDownOptions) {
    return { buyDownOptions, selectedBuyDownOptions };
  } else {
    buyDownOptions = loanResultData?.buyDownOptions;
    selectedBuyDownOptions = findSelectedBuyDownOptions(
      buyDownOptions,
      currentSelectedInterestRate
    );
    return { buyDownOptions, selectedBuyDownOptions };
  }
};

const findSelectedBuyDownOptions = (
  buyDownOptions: any,
  currentSelectedInterestRate: any
) => {
  return buyDownOptions?.find((item: any) => {
    const itemInterestRate = item?.interestRate
      ? parseFloat(item.interestRate) * 100
      : null;
    const selectedInterestRate = currentSelectedInterestRate?.interestRate
      ? parseFloat(currentSelectedInterestRate?.interestRate.replace("%", ""))
      : null;

    if (itemInterestRate === null || selectedInterestRate === null) {
      return false;
    }
    return itemInterestRate?.toFixed(4) === selectedInterestRate?.toFixed(4);
  });
};

// ===================================================================
// Preamble/context resolver helpers (extracted from getTermSheetData /
// getSuggestedLoanTerms to reduce cognitive complexity)
// ===================================================================
function resolveNewDataToBeReffered(loanDetails: any, quickLoanSizingData: any, detailedLoanSizingData: any) {
  let newDataToBeReffered: any = {
    ...loanDetails,
  };
  if (newDataToBeReffered?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING) {
    newDataToBeReffered = {
      ...newDataToBeReffered,
      ...quickLoanSizingData,
    };
  } else if (
    newDataToBeReffered?.sizingType === SizingTypeValue?.DETAILED_LOAN_SIZING
  ) {
    newDataToBeReffered = {
      ...newDataToBeReffered,
      ...detailedLoanSizingData,
    };
  }
  return newDataToBeReffered;
}

function resolvePropertyDetails(loanData: any, newDataToBeReffered: any) {
  return (
 loanData?.propertyDetails?.length
    ? loanData?.propertyDetails
    : loanData?.loanDetails?.propertyDetails ??
      newDataToBeReffered.propertyDetails
  );
}

function resolveLeverageAndPricingData(loanData: any) {
  return (
 Object.keys(
    loanData?.leverageAndPricingData ?? {}
  )?.length
    ? loanData?.leverageAndPricingData
    : loanData?.loanDetails?.leverageAndPricingData
  );
}

function resolveLoanId(loanID: any, loanData: any) {
  return (
 loanID ? loanID : loanData?.loansApiResponse?.loanId || ""
  );
}

function resolveLoanResultDataForPartner(loanData: any, loanDetails: any, assignedPartner: any, loanId: any) {
  return (
loanData?.rulesResponse?.[loanDetails?.takeOutPartner]
      ?.loanResult?.[loanId]
      ? loanData?.rulesResponse?.[loanDetails?.takeOutPartner]?.loanResult?.[
          loanId
        ]
      : loanData?.rulesResponse?.tpResultMap?.[assignedPartner]
      ? loanData?.rulesResponse?.tpResultMap?.[assignedPartner]?.loanResult?.[
          loanId
        ]
      : loanDetails?.rulesResponse?.tpResultMap?.[assignedPartner]
          ?.loanResult?.[loanId]
  );
}

function resolveLoanResultDataForInvestorDscr(loanData: any, loanDetails: any, newDataToBeReffered: any, loanId: any, firstPartner: any) {
  return (
loanData?.rulesResponse?.[loanDetails?.takeOutPartner]
        ?.loanResult?.[loanId]
        ? loanData?.rulesResponse?.[loanDetails?.takeOutPartner]?.loanResult?.[
            loanId
          ]
        : loanData?.rulesResponse?.tpResultMap
        ? loanData?.rulesResponse?.tpResultMap?.[
            Object.keys(loanDetails?.rulesResponse?.tpResultMap ?? {})?.[0]
          ]?.loanResult?.[loanId]
        : loanDetails?.rulesResponse?.tpResultMap?.[
            Object.keys(loanDetails?.rulesResponse?.tpResultMap ?? {})?.[0]
          ]?.loanResult?.[loanId]
        ? loanDetails?.rulesResponse?.tpResultMap?.[
            Object.keys(loanDetails?.rulesResponse?.tpResultMap ?? {})?.[0]
          ]?.loanResult?.[loanId]
        : newDataToBeReffered?.partnersResults?.loanResult?.[loanId] ??
          loanData?.rulesResponse?.[firstPartner]?.loanResult?.[loanId]
  );
}

function resolveLoanResultData(loanData: any, loanDetails: any, assignedPartner: any, loanType: any, loanId: any, newDataToBeReffered: any) {
  if (assignedPartner) {
    return resolveLoanResultDataForPartner(loanData, loanDetails, assignedPartner, loanId);
  }
  const firstPartner = Object.keys(
      loanData?.rulesResponse?.tpResultMap ?? {}
    )?.[0];
  if (loanType === "Investor DSCR") {
    return resolveLoanResultDataForInvestorDscr(loanData, loanDetails, newDataToBeReffered, loanId, firstPartner);
  }
  return loanData?.rulesResponse?.loanResult?.[loanId];
}

function resolveCurrentSelectedInterestRate(loanData: any, newDataToBeReffered: any) {
  return (
    loanData?.currentSelectedInterestRate ??
    newDataToBeReffered?.currentSelectedInterestRate
  );
}

function resolveMaxAsIsLtv(loanResultData: any, selectedBuyDownOptions: any) {
  return (
    // loanResultData?.maxLeverage?.maxAsIsLTV &&
    // !isNaN(+loanResultData?.maxLeverage?.maxAsIsLTV)
    //   ? `${+loanResultData?.maxLeverage?.maxAsIsLTV * 100} %`
    loanResultData?.toorakProceeds?.toorakAdjustedAsIsLtv &&
    !isNaN(+loanResultData?.toorakProceeds?.toorakAdjustedAsIsLtv)
      ? `${+loanResultData?.toorakProceeds?.toorakAdjustedAsIsLtv * 100} %`
      : !isNaN(+selectedBuyDownOptions?.toorakAdjustedAsIsLtv)
      ? `${+selectedBuyDownOptions?.toorakAdjustedAsIsLtv * 100} %`
      : "--"
  );
}

function resolveMonthlyQualifyingPayment(selectedBuyDownOptions: any, loanResultData: any) {
  return (
    selectedBuyDownOptions?.monthlyQualifyingPayment ||
    loanResultData?.loanEconomics?.monthlyQualifyingPayment
  );
}

function resolveMonthlyQualifyingPI(selectedBuyDownOptions: any, loanResultData: any) {
  return (
    selectedBuyDownOptions?.monthlyQualifyingPI ||
    loanResultData?.loanEconomics?.monthlyQualifyingPI
  );
}

function resolveRequestedLTVARV(loanResultData: any) {
  return (
    loanResultData?.loanFeatures?.originalAsIsRepairedLtv ||
    loanResultData?.loanFeatures?.arvLtvAsCompleted
  );
}

function resolveTotalBudget(newDataToBeReffered: any, loanResultData: any) {
  return (
    newDataToBeReffered?.loanEconomics?.totalBudget ||
    loanResultData?.guidelineOutput?.totalBudget
  );
}

function resolveLoanPurpose(newDataToBeReffered: any, loanResultData: any, loanData: any, assignedPartner: any, loanId: any) {
  return (
    newDataToBeReffered?.autoCalculatedLoanResults?.loanCharacterisation
      ?.loanPurpose ||
    loanResultData?.loanCharacterisation?.loanPurpose ||
    loanResultData?.loanCharacterization?.finalLoanCharacterization ||
    loanData?.rulesResponse?.tpResultMap?.[assignedPartner]?.loanResult?.[
      loanId
    ]?.loanCharacterization?.finalLoanCharacterization
  );
}

function resolvePropertyAddress(propertyDetails: any, newDataToBeReffered: any) {
  return (
 cleanedAdressString(
    propertyDetails?.[0]?.propertyLocation ??
      newDataToBeReffered?.propertyLocation
  )
  );
}

function resolveIndexOfMostRestrictiveProperty(loanData: any) {
  return (
    loanData?.autoCalculatedLoanResults?.loanCharacterisation
      ?.indexOfMostRestrictiveProperty ?? 0
  );
}

function resolvePrepaymentPenaltyTypeMonths(newDataToBeReffered: any) {
  return (
    newDataToBeReffered?.loanEconomics?.prepaymentPenaltyTypeMonths ?? 0
  );
}

function resolvePrepayPenaltyType(prepaymentPenaltyTypeMonths: any, newDataToBeReffered: any) {
  return (
    prepaymentPenaltyTypeMonths || prepaymentPenaltyTypeMonths === 0
      ? `${prepaymentPenaltyTypeMonths} Months - ${
          newDataToBeReffered?.loanEconomics?.prepayPenaltyType ??
          prePayTypeToMonthsMap?.[prepaymentPenaltyTypeMonths]
        }`
      : "--"
  );
}

function buildDiscountPointsRateInput(currentSelectedInterestRate: any, selectedBuyDownOptions: any) {
  return     {
      ...currentSelectedInterestRate,
      discountPoints:
        selectedBuyDownOptions?.discountPointPercentage != null
          ? `${selectedBuyDownOptions?.discountPointPercentage?.toFixed(4)} %`
          : null,
      discountPointsWithDollar:
        selectedBuyDownOptions?.discountPointAmount != null
          ? sanitizeCurrency(selectedBuyDownOptions?.discountPointAmount)
          : null,
    };
}

function resolveEstimatedMinimumReserves(selectedBuyDownOptions: any, leverageAndPricingData: any) {
  return (
    selectedBuyDownOptions?.minimumBorrowerLiquidity ||
    leverageAndPricingData?.requiredReserves?.minimumRequiredReserves
  );
}

function resolveNoteRate(newDataToBeReffered: any) {
  return (
 newDataToBeReffered?.loanEconomics?.originalInterestRate
    ? `${parseFloat(
        newDataToBeReffered?.loanEconomics?.originalInterestRate
      )?.toFixed(3)} %`
    : newDataToBeReffered?.loanEconomics?.dscrInterestRate
    ? `${parseFloat(
        newDataToBeReffered?.loanEconomics?.dscrInterestRate
      )?.toFixed(3)} %`
    : newDataToBeReffered?.currentSelectedInterestRate?.interestRate
    ? `${newDataToBeReffered?.currentSelectedInterestRate?.interestRate}`
    : newDataToBeReffered?.loanEconomics?.interestRate
    ? `${parseFloat(newDataToBeReffered?.loanEconomics?.interestRate)?.toFixed(
        3
      )} %`
    : "--"
  );
}

function resolveLoanTypeStr(loanType: any, fetchData: any, propertyDetails: any) {
  return (
    loanType === "Investor DSCR"
      ? "Investor DSCR"
      : getBridgeLoanType(fetchData, propertyDetails)
  );
}

function resolveSuggestedLeverageAndPricingData(loanData: any) {
  return (
    loanData?.leverageAndPricingData ||
    loanData?.loanDetails?.leverageAndPricingData
  );
}

function resolveAmountRequested(tableBody: any) {
  let tempAmountRequested = tableBody?.[2]?.data?.slice(-1)[0];

  if (tempAmountRequested && typeof tempAmountRequested === "string") {
    tempAmountRequested = tempAmountRequested.replace(/-/g, "");
  }
  const amountRequested: any =
    !isNaN(Number(tempAmountRequested)) &&
    typeof Number(tempAmountRequested) === "number"
      ? sanitizeCurrency(tempAmountRequested)
      : tempAmountRequested;
  return amountRequested;
}

// ===================================================================
// Per-field value resolvers (one per term sheet output field, extracted
// to keep each function's cognitive complexity low; several are shared
// across loan-type cases where the original source expression was
// byte-for-byte identical)
// ===================================================================
function fieldPropertyAddress(propertyAddress: any) {
  return (
 propertyAddress ? propertyAddress : "--"
  );
}

function fieldBridgeLoanTermMonths(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.loanTerm ||
                termSheetData?.termSheetLoanInfo?.["Loan Term(Months)"]
              : loanDetails?.loanInformation?.loanTerm
              ? `${loanDetails?.loanInformation?.loanTerm} Months`
              : "12 Months"
  );
}

function fieldBridgeLoanPurposeFromLoanDetails(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.loanPurpose ||
                termSheetData?.termSheetLoanInfo?.["Loan Purpose"]
              : loanDetails?.loanInformation?.loanPurpose
              ? `${loanDetails?.loanInformation?.loanPurpose}`
              : "--"
  );
}

function fieldGuarantorsExperienceFromCompleted36Months(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.guarantorsExperience ||
                termSheetData?.termSheetLoanInfo?.["Guarantor's Experience"]
              : loanDetails?.borrowerInformation
                  ?.propertiesCompletedWithinLast36Months ?? "--"
  );
}

function fieldBridgeCreditScore(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.creditScore ||
                termSheetData?.termSheetLoanInfo?.["Credit Score"]
              : loanDetails?.borrowerInformation?.originalCreditScore ?? "--"
  );
}

function fieldBridgeProductType(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.baseProgram ||
                termSheetData?.termSheetLoanInfo?.["Product Type"]
              : loanDetails?.productType
  );
}

function fieldTotalRehabBudgetFromLoanDetails(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.rehabCosts ||
                    termSheetData?.termSheetLoanInfo?.["Total Rehab Budget"]
                )
              : loanDetails?.loanEconomics?.totalRehabBudget
              ? sanitizeCurrency(loanDetails?.loanEconomics?.totalRehabBudget)
              : "--"
  );
}

function fieldBridgeRequestedLtvGroup(isViewOrDownload: any, termSheetData: any, requestedLTVAsIs: any, requestedLTVARV: any, requestedLTC: any) {
  return {
            "Requested LTV(As is)": isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.requestedLTVAsIs ||
                    termSheetData?.termSheetLoanInfo?.["Requested LTV(As is)"]
                )
              : requestedLTVAsIs && !Number.isNaN(+requestedLTVAsIs)
              ? `${(requestedLTVAsIs * 100)?.toFixed(2)} %`
              : "--",
            "Requested LTV(ARV)": isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.requestedLTVARV ||
                    termSheetData?.termSheetLoanInfo?.["Requested LTV(ARV)"]
                )
              : requestedLTVARV
              ? `${(requestedLTVARV * 100)?.toFixed(2)} %`
              : "--",
            "Requested LTC": isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.requestedLTC ||
                    termSheetData?.termSheetLoanInfo?.["Requested LTC"]
                )
              : requestedLTC
              ? `${(requestedLTC * 100)?.toFixed(2)} %`
              : "--",
  };
}

function fieldPropertyTypeNoOptionalChain(isViewOrDownload: any, termSheetData: any, propertyDetails: any, indexOfMostRestrictiveProperty: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.propertyType ||
                termSheetData?.termSheetLoanInfo?.["Property Type"]
              : propertyDetails[indexOfMostRestrictiveProperty]
                  ?.propertyInformation?.propertyType ?? "--"
  );
}

function fieldBridgeCitizenshipGated(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.citizenshipStatus ||
                termSheetData?.termSheetLoanInfo?.["Citizenship Status"]
              : loanDetails?.borrowerInformation?.borrowerGuarantor
              ? loanDetails?.borrowerInformation?.borrowerGuarantor === "Yes"
                ? loanDetails?.borrowerInformation?.immigrationStatus
                : "U.S. Citizen"
              : "--"
  );
}

function fieldPurchasePriceNoOptionalChain(isViewOrDownload: any, termSheetData: any, propertyDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.purchasePrice ||
                    termSheetData?.termSheetLoanInfo?.["Purchase Price"]
                )
              : propertyDetails[0]?.propertyEconomics?.purchasePrice
              ? sanitizeCurrency(
                  propertyDetails[0]?.propertyEconomics?.purchasePrice
                )
              : "--"
  );
}

function investorDscrRequestedLoanAmount(isViewOrDownload: any, termSheetData: any, newDataToBeReffered: any) {
  return (
              (isViewOrDownload
                ? sanitizeCurrency(
                    termSheetData?.termSheetLoanInfo?.loanAmount ||
                      termSheetData?.termSheetLoanInfo?.[
                        "Requested Loan Amount"
                      ]
                  )
                : sanitizeCurrency(
                    newDataToBeReffered?.loanEconomics?.borrowerRequested ||
                      newDataToBeReffered?.loanInformation?.borrowerRequested
                  )) || "--"
  );
}

function investorDscrRequestedLtv(isViewOrDownload: any, termSheetData: any, requestedLTVAsIs: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.requestedLTVAsIs ||
                    termSheetData?.termSheetLoanInfo?.["Requested LTV"]
                )
              : requestedLTVAsIs
              ? `${(requestedLTVAsIs * 100)?.toFixed(2)}%`
              : "--"
  );
}

function investorDscrMaxLtv(isViewOrDownload: any, termSheetData: any, maxAsIsLtv: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.maxLTV ||
                termSheetData?.termSheetLoanInfo?.["Max LTV"]
              : maxAsIsLtv !== "--"
              ? sanitizePercentage(maxAsIsLtv, false, 2)
              : "--"
  );
}

function investorDscrDscrField(isViewOrDownload: any, isMinDataView: any, termSheetData: any, newDataToBeReffered: any, selectedBuyDownOptions: any, leverageAndPricingData: any, autoCalculatedLoanResults: any) {
  return (
              (isViewOrDownload &&
                (termSheetData?.termSheetLoanInfo?.dscr ||
                  termSheetData?.termSheetLoanInfo?.["DSCR"])) ||
              (isMinDataView &&
                (newDataToBeReffered?.loanEconomics?.dscr ?? null)) ||
              selectedBuyDownOptions?.dscr ||
              newDataToBeReffered?.leverageAndPricingData?.leverageCards
                ?.dscr ||
              leverageAndPricingData?.leverageCards?.dscr ||
              newDataToBeReffered?.loanOutputs?.dscr ||
              autoCalculatedLoanResults?.loanEconomics?.dscr ||
              "--"
  );
}

function investorDscrNoteRate(isViewOrDownload: any, termSheetData: any, currentRateData: any, noteRate: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.interestRate ||
                termSheetData?.termSheetLoanInfo?.["Note Rate"]
              : currentRateData?.interestRate ?? noteRate
  );
}

function investorDscrCreditScore(isViewOrDownload: any, termSheetData: any, newDataToBeReffered: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.fico ||
                termSheetData?.termSheetLoanInfo?.["Credit Score"]
              : newDataToBeReffered?.borrowerInformation?.creditScoreActual
  );
}

function investorDscrPropertyType(isViewOrDownload: any, termSheetData: any, propertyDetails: any, indexOfMostRestrictiveProperty: any, newDataToBeReffered: any) {
  return (
              (isViewOrDownload
                ? termSheetData?.termSheetLoanInfo?.propertyType ||
                  termSheetData?.termSheetLoanInfo?.["Property Type"]
                : propertyDetails?.[indexOfMostRestrictiveProperty]
                    ?.propertyInformation?.propertyType) ??
              newDataToBeReffered?.propertyInformation?.propertyType ??
              "--"
  );
}

function investorDscrPropertyUsage(isViewOrDownload: any, termSheetData: any, propertyDetails: any, indexOfMostRestrictiveProperty: any) {
  return (
              (isViewOrDownload
                ? termSheetData?.termSheetLoanInfo?.propertyUsage ||
                  termSheetData?.termSheetLoanInfo?.["Property Usage"]
                : propertyDetails?.[indexOfMostRestrictiveProperty]
                    ?.unitInformation?.[0]?.currentUnitUsage) ||
              "Investment Property"
  );
}

function investorDscrRentalStatus(isViewOrDownload: any, termSheetData: any, propertyDetails: any, indexOfMostRestrictiveProperty: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.rentalCharacterization ||
                termSheetData?.termSheetLoanInfo?.["Rental Status"]
              : `${
                  propertyDetails?.[indexOfMostRestrictiveProperty]
                    ?.unitInformation?.[0]?.rentalCharacterization ||
                  propertyDetails?.[0]?.propertyInformation
                    ?.rentalCharacterization ||
                  "--"
                }` || "--"
  );
}

function investorDscrEstimatedMinReserves(isViewOrDownload: any, termSheetData: any, estimatedMinimumReserves: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.minNoOfMonthsReserve ??
                    termSheetData?.termSheetLoanInfo?.[
                      "Estimated Minimum Reserves"
                    ]
                )
              : estimatedMinimumReserves
              ? sanitizeCurrency(estimatedMinimumReserves)
              : "--"
  );
}

function investorDscrEstimatedAsIsValue(isViewOrDownload: any, termSheetData: any, propertyDetails: any, indexOfMostRestrictiveProperty: any, newDataToBeReffered: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.asIsValue ||
                    termSheetData?.termSheetLoanInfo?.["Estimated As-Is-Value"]
                )
              : propertyDetails?.[indexOfMostRestrictiveProperty]
                  ?.propertyEconomics?.asIsValue
              ? sanitizeCurrency(
                  propertyDetails?.[0]?.propertyEconomics?.asIsValue
                )
              : propertyDetails?.[0]?.propertyInformation?.asIsValue
              ? sanitizeCurrency(
                  propertyDetails?.[0]?.propertyInformation?.asIsValue
                )
              : newDataToBeReffered?.propertyInformation?.asIsValue
              ? sanitizeCurrency(
                  newDataToBeReffered?.propertyInformation?.asIsValue
                )
              : "--"
  );
}

function investorDscrMonthlyPaymentField(isViewOrDownload: any, termSheetData: any, monthlyQualifyingPayment: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.monthlyQualifyingPayment ||
                termSheetData?.termSheetLoanInfo?.[
                  "Monthly Payment including Taxes and Insurance"
                ]
                ? `${sanitizeCurrency(
                    termSheetData?.termSheetLoanInfo
                      ?.monthlyQualifyingPayment ||
                      termSheetData?.termSheetLoanInfo?.[
                        "Monthly Payment including Taxes and Insurance"
                      ]
                  )}`
                : "--"
              : monthlyQualifyingPayment
              ? sanitizeCurrency(monthlyQualifyingPayment)
              : "--"
  );
}

function investorDscrMonthlyPiField(isViewOrDownload: any, termSheetData: any, monthlyQualifyingPI: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.monthlyQualifyingPI ||
                termSheetData?.termSheetLoanInfo?.[
                  "Monthly Principal and Interest or Interest Only"
                ]
                ? `${sanitizeCurrency(
                    termSheetData?.termSheetLoanInfo?.monthlyQualifyingPI ||
                      termSheetData?.termSheetLoanInfo?.[
                        "Monthly Principal and Interest or Interest Only"
                      ]
                  )}`
                : "--"
              : monthlyQualifyingPI
              ? sanitizeCurrency(monthlyQualifyingPI)
              : "--"
  );
}

function investorDscrLoanTermField(isViewOrDownload: any, termSheetData: any, newDataToBeReffered: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.loanTerm ||
                termSheetData?.termSheetLoanInfo?.["Loan Term(Months)"]
                ? `${
                    termSheetData?.termSheetLoanInfo?.loanTerm ||
                    termSheetData?.termSheetLoanInfo?.["Loan Term(Months)"]
                  }`
                : "360 Months"
              : newDataToBeReffered?.loanInformation?.loanTerm
              ? `${newDataToBeReffered?.loanInformation?.loanTerm} Months`
              : "360 Months"
  );
}

function investorDscrIoPeriodField(isViewOrDownload: any, termSheetData: any, newDataToBeReffered: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.ioPeriod ||
                termSheetData?.termSheetLoanInfo?.[
                  "Interest-only Period (If applicable)"
                ]
              : newDataToBeReffered?.loanEconomics?.ioPeriod
              ? `${newDataToBeReffered?.loanEconomics?.ioPeriod} Months`
              : "NA"
  );
}

function investorDscrPrePaymentPenaltyField(isViewOrDownload: any, termSheetData: any, prepayPenaltyType: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.prePaymentPenalty ||
                termSheetData?.termSheetLoanInfo?.["Pre-Payment Penalty"]
              : prepayPenaltyType || "--"
  );
}

function investorDscrProductTypeField(isViewOrDownload: any, termSheetData: any, loanTypeStr: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.loanType ||
                termSheetData?.termSheetLoanInfo?.["Product Type"]
              : loanTypeStr || "--"
  );
}

function investorDscrLoanPurposeField(isViewOrDownload: any, termSheetData: any, newDataToBeReffered: any, loanPurpose: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.loanPurpose ||
                termSheetData?.termSheetLoanInfo?.["Loan Purpose"]
              : `${
                  newDataToBeReffered?.loanInformation?.loanPurpose ||
                  loanPurpose ||
                  "--"
                }` || "--"
  );
}

function investorDscrCitizenshipField(isViewOrDownload: any, termSheetData: any, newDataToBeReffered: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.citizenshipStatus ||
                termSheetData?.termSheetLoanInfo?.["Citizenship Status"]
              : newDataToBeReffered?.borrowerInformation?.borrowerGuarantor
              ? newDataToBeReffered?.borrowerInformation?.borrowerGuarantor ===
                "Yes"
                ? newDataToBeReffered?.borrowerInformation?.immigrationStatus
                : "U.S. Citizen"
              : newDataToBeReffered?.borrowerInformation?.citizenshipStatus
              ? newDataToBeReffered?.borrowerInformation?.citizenshipStatus ===
                "No"
                ? "U.S. Citizen"
                : newDataToBeReffered?.borrowerInformation
                    ?.citizenshipStatus === "Yes"
                ? "Foreign National"
                : newDataToBeReffered?.borrowerInformation?.citizenshipStatus
              : "--"
  );
}

function investorDscrPurchasePriceField(isViewOrDownload: any, termSheetData: any, propertyDetails: any, newDataToBeReffered: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.purchasePrice ||
                    termSheetData?.termSheetLoanInfo?.["Purchase Price"]
                )
              : propertyDetails?.[0]?.propertyEconomics
                  ?.propertyAcquisitionPrice
              ? sanitizeCurrency(
                  propertyDetails?.[0]?.propertyEconomics
                    ?.propertyAcquisitionPrice
                )
              : propertyDetails?.[0]?.propertyEconomics?.purchasePrice
              ? sanitizeCurrency(
                  propertyDetails?.[0]?.propertyEconomics?.purchasePrice
                )
              : newDataToBeReffered?.autoCalculatedPropResults?.[0]
                  ?.loanFeatures?.adjustedAsIsValue
              ? sanitizeCurrency(
                  newDataToBeReffered?.autoCalculatedPropResults?.[0]
                    ?.loanFeatures?.adjustedAsIsValue
                )
              : newDataToBeReffered?.loanInformation?.purchasePrice
              ? sanitizeCurrency(
                  newDataToBeReffered?.loanInformation?.purchasePrice
                )
              : "--"
  );
}

function investorDscrDiscountPointsPctField(isViewOrDownload: any, termSheetData: any, discountPoints: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.["Discount Points (%)"]
              : discountPoints
              ? `${discountPoints}`
              : "--"
  );
}

function investorDscrDiscountPointsDollarField(isViewOrDownload: any, termSheetData: any, discountPointsWithDollar: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.["Discount Points ($)"]
                )
              : discountPointsWithDollar
              ? `${sanitizeCurrency(discountPointsWithDollar)}`
              : "--"
  );
}

function bridgeLoan24PropertyTypeField(isViewOrDownload: any, termSheetData: any, propertyDetails: any, indexOfMostRestrictiveProperty: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.propertyType ||
                termSheetData?.termSheetLoanInfo?.["Property Type"]
              : propertyDetails?.[indexOfMostRestrictiveProperty]
                  ?.propertyInformation?.propertyType ?? "--"
  );
}

function bridgeLoan24CitizenshipField(termSheetData: any, loanDetails: any) {
  return (
              loanDetails?.borrowerInformation?.borrowerGuarantor ||
              termSheetData?.termSheetLoanInfo?.["Citizenship Status"]
                ? loanDetails?.borrowerInformation?.borrowerGuarantor === "Yes"
                  ? loanDetails?.borrowerInformation?.immigrationStatus
                  : "U.S. Citizen"
                : "--"
  );
}

function bridgeLoan24AsIsValueField(isViewOrDownload: any, termSheetData: any, propertyDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.asIsValue ||
                    termSheetData?.termSheetLoanInfo?.["Estimated As-Is-Value"]
                )
              : propertyDetails?.[0]?.propertyEconomics?.orgAppraisalValue
              ? sanitizeCurrency(
                  propertyDetails?.[0]?.propertyEconomics?.orgAppraisalValue
                )
              : "--"
  );
}

function bridgeLoan24PurchasePriceField(isViewOrDownload: any, termSheetData: any, propertyDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.purchasePrice ||
                    termSheetData?.termSheetLoanInfo?.["Purchase Price"]
                )
              : propertyDetails?.[0]?.propertyEconomics?.purchasePrice
              ? sanitizeCurrency(
                  propertyDetails?.[0]?.propertyEconomics?.purchasePrice
                )
              : "--"
  );
}

function bridgeLoan24ArvField(isViewOrDownload: any, termSheetData: any, propertyDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.afterRepairValueARV ||
                    termSheetData?.termSheetLoanInfo?.[
                      "After Repair Value (ARV)"
                    ]
                )
              : propertyDetails?.[0]?.propertyEconomics?.asRepairedValueARV
              ? sanitizeCurrency(
                  propertyDetails?.[0]?.propertyEconomics?.asRepairedValueARV
                )
              : "--"
  );
}

function bridgeLoanGroundUpLoanPurposeField(isViewOrDownload: any, termSheetData: any, loanPurpose: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.loanPurpose ||
                termSheetData?.termSheetLoanInfo?.["Loan Purpose"]
              : loanPurpose
              ? `${loanPurpose}`
              : "--"
  );
}

function bridgeLoanGroundUpGuarantorsExperienceField(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.guarantorsExperience ||
                termSheetData?.termSheetLoanInfo?.["Guarantor's Experience"]
              : loanDetails?.borrowerInformation?.borrowerGUCExperience ?? "--"
  );
}

function bridgeLoanGroundUpAsIsValueField(isViewOrDownload: any, termSheetData: any, propertyDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.asIsValue ||
                    termSheetData?.termSheetLoanInfo?.["Estimated As-Is-Value"]
                )
              : propertyDetails[0]?.propertyEconomics?.asIsValue
              ? sanitizeCurrency(
                  propertyDetails[0]?.propertyEconomics?.asIsValue
                )
              : "--"
  );
}

function bridgeLoanGroundUpTotalRehabBudgetField(isViewOrDownload: any, termSheetData: any, totalBudget: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.rehabCosts ||
                    termSheetData?.termSheetLoanInfo?.["Total Rehab Budget"]
                )
              : totalBudget && !isNaN(totalBudget)
              ? sanitizeCurrency(totalBudget)
              : "--"
  );
}

function bridgeLoanGroundUpArvField(isViewOrDownload: any, termSheetData: any, propertyDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.afterRepairValueARV ||
                    termSheetData?.termSheetLoanInfo?.[
                      "After Repair Value (ARV)"
                    ]
                )
              : propertyDetails[0]?.propertyEconomics?.afterRepairValue
              ? sanitizeCurrency(
                  propertyDetails[0]?.propertyEconomics?.afterRepairValue
                )
              : "--"
  );
}

function bridgeLoanMultifamilyAsIsValueField(isViewOrDownload: any, termSheetData: any, propertyDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.asIsValue ||
                    termSheetData?.termSheetLoanInfo?.["Estimated As-Is-Value"]
                )
              : propertyDetails[0]?.propertyEconomics?.orgAppraisalValue
              ? sanitizeCurrency(
                  propertyDetails[0]?.propertyEconomics?.orgAppraisalValue
                )
              : "--"
  );
}

function bridgeLoanMultifamilyArvField(isViewOrDownload: any, termSheetData: any, propertyDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.afterRepairValueARV ||
                    termSheetData?.termSheetLoanInfo?.[
                      "After Repair Value (ARV)"
                    ]
                )
              : propertyDetails[0]?.propertyEconomics?.asRepairedValueARV
              ? sanitizeCurrency(
                  propertyDetails[0]?.propertyEconomics?.asRepairedValueARV
                )
              : "--"
  );
}

function fieldInitialLoanAmountStandard(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.loanAmount ||
                    termSheetData?.termSheetLoanInfo?.["Initial Loan Amount"]
                )
              : sanitizeCurrency(
                  loanDetails?.loanEconomics?.initialLoanAmount
                ) || "--"
  );
}

function fieldFinancedBudget(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.rehabBudgetLenderFunded ||
                    termSheetData?.termSheetLoanInfo?.["Financed Budget"]
                )
              : loanDetails?.loanEconomics?.financedBudget
              ? sanitizeCurrency(loanDetails?.loanEconomics?.financedBudget)
              : "--"
  );
}

function fieldSuggestedRequestedLoanAmount(isViewOrDownload: any, termSheetData: any, leverageAndPricingData: any, amountRequested: any) {
  return (
 isViewOrDownload
              ? sanitizeCurrency(
                  termSheetData?.termSheetLoanInfo?.amountRequested ||
                    termSheetData?.termSheetLoanInfo?.["Requested Loan Amount"]
                )
              : leverageAndPricingData?.proceeds
              ? amountRequested
              : "--"
  );
}

function bridgeLoan24SuggestedNoteRateField(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.interestRate ||
                termSheetData?.termSheetLoanInfo?.["Note Rate"]
              : loanDetails?.loanEconomics?.originalInterestRate ||
                loanDetails?.loanEconomics?.interestRate
              ? `${
                  loanDetails?.loanEconomics?.originalInterestRate ||
                  loanDetails?.loanEconomics?.interestRate
                } %`
              : "--"
  );
}

function bridgeLoanGroundUpInitialLoanAmountField(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.loanAmount ||
                termSheetData?.termSheetLoanInfo?.["Initial Loan Amount"]
              : sanitizeCurrency(
                  loanDetails?.loanEconomics?.initialLoanAmount
                ) || "--"
  );
}

function fieldSuggestedNoteRateStandard(isViewOrDownload: any, termSheetData: any, loanDetails: any) {
  return (
 isViewOrDownload
              ? termSheetData?.termSheetLoanInfo?.interestRate ||
                termSheetData?.termSheetLoanInfo?.["Note Rate"]
              : loanDetails?.loanEconomics?.interestRate
              ? `${loanDetails?.loanEconomics?.interestRate} %`
              : "--"
  );
}
// ===================================================================
// Row/case builder functions for getTermSheetData
// ===================================================================

function buildInvestorDscrRow1(ctx: any) {
  return {
    "Loan Id": ctx.evaluationID,
    "Property Address": fieldPropertyAddress(ctx.propertyAddress),
    "Requested Loan Amount": investorDscrRequestedLoanAmount(ctx.isViewOrDownload, ctx.termSheetData, ctx.newDataToBeReffered),
    "Requested LTV": investorDscrRequestedLtv(ctx.isViewOrDownload, ctx.termSheetData, ctx.requestedLTVAsIs),
    "Max LTV": investorDscrMaxLtv(ctx.isViewOrDownload, ctx.termSheetData, ctx.maxAsIsLtv),
    DSCR: investorDscrDscrField(ctx.isViewOrDownload, ctx.isMinDataView, ctx.termSheetData, ctx.newDataToBeReffered, ctx.selectedBuyDownOptions, ctx.leverageAndPricingData, ctx.autoCalculatedLoanResults),
    "Note Rate": investorDscrNoteRate(ctx.isViewOrDownload, ctx.termSheetData, ctx.currentRateData, ctx.noteRate),
    "Credit Score": investorDscrCreditScore(ctx.isViewOrDownload, ctx.termSheetData, ctx.newDataToBeReffered),
    "Property Type": investorDscrPropertyType(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails, ctx.indexOfMostRestrictiveProperty, ctx.newDataToBeReffered),
    "Property Usage": investorDscrPropertyUsage(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails, ctx.indexOfMostRestrictiveProperty),
    "Rental Status": investorDscrRentalStatus(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails, ctx.indexOfMostRestrictiveProperty),
    "Estimated Minimum Reserves": investorDscrEstimatedMinReserves(ctx.isViewOrDownload, ctx.termSheetData, ctx.estimatedMinimumReserves),
    "Estimated As-Is-Value": investorDscrEstimatedAsIsValue(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails, ctx.indexOfMostRestrictiveProperty, ctx.newDataToBeReffered),
  };
}

function buildInvestorDscrRow2(ctx: any) {
  return {
    "Monthly Payment including Taxes and Insurance": investorDscrMonthlyPaymentField(ctx.isViewOrDownload, ctx.termSheetData, ctx.monthlyQualifyingPayment),
    "Monthly Principal and Interest or Interest Only": investorDscrMonthlyPiField(ctx.isViewOrDownload, ctx.termSheetData, ctx.monthlyQualifyingPI),
    "Loan Term(Months)": investorDscrLoanTermField(ctx.isViewOrDownload, ctx.termSheetData, ctx.newDataToBeReffered),
    "Interest-only Period (If applicable)": investorDscrIoPeriodField(ctx.isViewOrDownload, ctx.termSheetData, ctx.newDataToBeReffered),
    "Lien Position": "First Lien",
    "Pre-Payment Penalty": investorDscrPrePaymentPenaltyField(ctx.isViewOrDownload, ctx.termSheetData, ctx.prepayPenaltyType),
    "Product Type": investorDscrProductTypeField(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanTypeStr),
    "Loan Purpose": investorDscrLoanPurposeField(ctx.isViewOrDownload, ctx.termSheetData, ctx.newDataToBeReffered, ctx.loanPurpose),
    "Citizenship Status": investorDscrCitizenshipField(ctx.isViewOrDownload, ctx.termSheetData, ctx.newDataToBeReffered),
    "Purchase Price": investorDscrPurchasePriceField(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails, ctx.newDataToBeReffered),
    "Discount Points (%)": investorDscrDiscountPointsPctField(ctx.isViewOrDownload, ctx.termSheetData, ctx.discountPoints),
    "Discount Points ($)": investorDscrDiscountPointsDollarField(ctx.isViewOrDownload, ctx.termSheetData, ctx.discountPointsWithDollar),
  };
}

function buildInvestorDscrTermSheet(ctx: any) {
  return [[buildInvestorDscrRow1(ctx)], [buildInvestorDscrRow2(ctx)]];
}

function buildBridgeLoan24Row1(ctx: any) {
  return {
    "Loan Id": ctx.evaluationID,
    "Property Address": fieldPropertyAddress(ctx.propertyAddress),
    "Loan Term(Months)": fieldBridgeLoanTermMonths(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Loan Purpose": fieldBridgeLoanPurposeFromLoanDetails(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Property Type": bridgeLoan24PropertyTypeField(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails, ctx.indexOfMostRestrictiveProperty),
    "Guarantor's Experience": fieldGuarantorsExperienceFromCompleted36Months(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Citizenship Status": bridgeLoan24CitizenshipField(ctx.termSheetData, ctx.loanDetails),
    "Credit Score": fieldBridgeCreditScore(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
  };
}

function buildBridgeLoan24Row2(ctx: any) {
  return {
    "Estimated As-Is-Value": bridgeLoan24AsIsValueField(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails),
    "Product Type": fieldBridgeProductType(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Purchase Price": bridgeLoan24PurchasePriceField(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails),
    "Total Rehab Budget": fieldTotalRehabBudgetFromLoanDetails(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "After Repair Value (ARV)": bridgeLoan24ArvField(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails),
    ...fieldBridgeRequestedLtvGroup(ctx.isViewOrDownload, ctx.termSheetData, ctx.requestedLTVAsIs, ctx.requestedLTVARV, ctx.requestedLTC),
  };
}

function buildBridgeLoan24TermSheet(ctx: any) {
  return [[buildBridgeLoan24Row1(ctx)], [buildBridgeLoan24Row2(ctx)]];
}

function buildBridgeLoanGroundUpRow1(ctx: any) {
  return {
    "Loan Id": ctx.evaluationID,
    "Property Address": fieldPropertyAddress(ctx.propertyAddress),
    "Loan Term(Months)": fieldBridgeLoanTermMonths(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Loan Purpose": bridgeLoanGroundUpLoanPurposeField(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanPurpose),
    "Property Type": fieldPropertyTypeNoOptionalChain(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails, ctx.indexOfMostRestrictiveProperty),
    "Guarantor's Experience": bridgeLoanGroundUpGuarantorsExperienceField(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Citizenship Status": fieldBridgeCitizenshipGated(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Credit Score": fieldBridgeCreditScore(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
  };
}

function buildBridgeLoanGroundUpRow2(ctx: any) {
  return {
    "Estimated As-Is-Value": bridgeLoanGroundUpAsIsValueField(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails),
    "Product Type": fieldBridgeProductType(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Purchase Price": fieldPurchasePriceNoOptionalChain(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails),
    "Total Rehab Budget": bridgeLoanGroundUpTotalRehabBudgetField(ctx.isViewOrDownload, ctx.termSheetData, ctx.totalBudget),
    "After Repair Value (ARV)": bridgeLoanGroundUpArvField(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails),
    ...fieldBridgeRequestedLtvGroup(ctx.isViewOrDownload, ctx.termSheetData, ctx.requestedLTVAsIs, ctx.requestedLTVARV, ctx.requestedLTC),
  };
}

function buildBridgeLoanGroundUpTermSheet(ctx: any) {
  return [[buildBridgeLoanGroundUpRow1(ctx)], [buildBridgeLoanGroundUpRow2(ctx)]];
}

function buildBridgeLoanMultifamilyRow1(ctx: any) {
  return {
    "Loan Id": ctx.evaluationID,
    "Property Address": fieldPropertyAddress(ctx.propertyAddress),
    "Loan Term(Months)": fieldBridgeLoanTermMonths(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Loan Purpose": fieldBridgeLoanPurposeFromLoanDetails(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Property Type": fieldPropertyTypeNoOptionalChain(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails, ctx.indexOfMostRestrictiveProperty),
    "Guarantor's Experience": fieldGuarantorsExperienceFromCompleted36Months(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Citizenship Status": fieldBridgeCitizenshipGated(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Credit Score": fieldBridgeCreditScore(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
  };
}

function buildBridgeLoanMultifamilyRow2(ctx: any) {
  return {
    "Estimated As-Is-Value": bridgeLoanMultifamilyAsIsValueField(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails),
    "Product Type": fieldBridgeProductType(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Purchase Price": fieldPurchasePriceNoOptionalChain(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails),
    "Total Rehab Budget": fieldTotalRehabBudgetFromLoanDetails(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "After Repair Value (ARV)": bridgeLoanMultifamilyArvField(ctx.isViewOrDownload, ctx.termSheetData, ctx.propertyDetails),
    ...fieldBridgeRequestedLtvGroup(ctx.isViewOrDownload, ctx.termSheetData, ctx.requestedLTVAsIs, ctx.requestedLTVARV, ctx.requestedLTC),
  };
}

function buildBridgeLoanMultifamilyTermSheet(ctx: any) {
  return [[buildBridgeLoanMultifamilyRow1(ctx)], [buildBridgeLoanMultifamilyRow2(ctx)]];
}

// ===================================================================
// Row/case builder functions for getSuggestedLoanTerms
// ===================================================================

function buildBridgeLoan24SuggestedRow1(ctx: any) {
  return {
    "Initial Loan Amount": fieldInitialLoanAmountStandard(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Financed Budget": fieldFinancedBudget(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Requested Loan Amount": fieldSuggestedRequestedLoanAmount(ctx.isViewOrDownload, ctx.termSheetData, ctx.leverageAndPricingData, ctx.amountRequested),
  };
}

function buildBridgeLoan24SuggestedRow2(ctx: any) {
  return {
    "Note Rate": bridgeLoan24SuggestedNoteRateField(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Interest Type": "Fixed",
    "--": "--",
  };
}

function buildBridgeLoan24SuggestedTerms(ctx: any) {
  return [[buildBridgeLoan24SuggestedRow1(ctx)], [buildBridgeLoan24SuggestedRow2(ctx)]];
}

function buildBridgeLoanGroundUpSuggestedRow1(ctx: any) {
  return {
    "Initial Loan Amount": bridgeLoanGroundUpInitialLoanAmountField(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Financed Budget": fieldFinancedBudget(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Requested Loan Amount": fieldSuggestedRequestedLoanAmount(ctx.isViewOrDownload, ctx.termSheetData, ctx.leverageAndPricingData, ctx.amountRequested),
  };
}

function buildBridgeLoanGroundUpSuggestedRow2(ctx: any) {
  return {
    "Note Rate": fieldSuggestedNoteRateStandard(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Interest Type": "Fixed",
    "--": "--",
  };
}

function buildBridgeLoanGroundUpSuggestedTerms(ctx: any) {
  return [[buildBridgeLoanGroundUpSuggestedRow1(ctx)], [buildBridgeLoanGroundUpSuggestedRow2(ctx)]];
}

function buildBridgeLoanMultifamilySuggestedRow1(ctx: any) {
  return {
    "Initial Loan Amount": fieldInitialLoanAmountStandard(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Financed Budget": fieldFinancedBudget(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Requested Loan Amount": fieldSuggestedRequestedLoanAmount(ctx.isViewOrDownload, ctx.termSheetData, ctx.leverageAndPricingData, ctx.amountRequested),
  };
}

function buildBridgeLoanMultifamilySuggestedRow2(ctx: any) {
  return {
    "Note Rate": fieldSuggestedNoteRateStandard(ctx.isViewOrDownload, ctx.termSheetData, ctx.loanDetails),
    "Interest Type": "Fixed",
    "--": "--",
  };
}

function buildBridgeLoanMultifamilySuggestedTerms(ctx: any) {
  return [[buildBridgeLoanMultifamilySuggestedRow1(ctx)], [buildBridgeLoanMultifamilySuggestedRow2(ctx)]];
}

export function getTermSheetData(
  loanType: string,
  loanData: any,
  t: any,
  termSheetAction: string,
  termSheetData: any,
  defaultSelectedDataView: string,
  evaluationID: string | number,
  loanID: any,
  waiverRequest: any
) {
  const isViewOrDownload = ["view", "download", "view-from-list"].includes(
    termSheetAction
  );
  const isMinDataView = defaultSelectedDataView === "minimumDataView";

  const {
    loanDetails,
    fetchData,
    autoCalculatedLoanResults,
    assignedPartner,
    detailedLoanSizingData,
    quickLoanSizingData,
  } = loanData;

  const newDataToBeReffered = resolveNewDataToBeReffered(
    loanDetails,
    quickLoanSizingData,
    detailedLoanSizingData
  );
  const propertyDetails = resolvePropertyDetails(loanData, newDataToBeReffered);
  const leverageAndPricingData = resolveLeverageAndPricingData(loanData);
  const loanId = resolveLoanId(loanID, loanData);
  const loanResultData = resolveLoanResultData(
    loanData,
    loanDetails,
    assignedPartner,
    loanType,
    loanId,
    newDataToBeReffered
  );

  const currentSelectedInterestRate = resolveCurrentSelectedInterestRate(
    loanData,
    newDataToBeReffered
  );

  const { buyDownOptions, selectedBuyDownOptions } = getSelectedBuyDownOptions(
    loanData,
    loanResultData,
    currentSelectedInterestRate
  );

  // const buyDownOptions =
  //   loanResultData?.buyDownOptions ?? loanData?.buyDownOptions;
  // const selectedBuyDownOptions = buyDownOptions?.find((item: any) => {
  //   const itemInterestRate = item?.interestRate
  //   ? parseFloat(item.interestRate) * 100
  //   : null;
  //   const selectedInterestRate = currentSelectedInterestRate?.interestRate
  //   ? parseFloat(currentSelectedInterestRate?.interestRate.replace("%", ""))
  //   : null;

  //   console.log("item", {item, itemInterestRate, selectedInterestRate, status: itemInterestRate?.toFixed(4) === selectedInterestRate?.toFixed(4) })
  //   if (itemInterestRate === null || selectedInterestRate === null) {
  //     return false;
  //   }
  //   return itemInterestRate?.toFixed(4) === selectedInterestRate?.toFixed(4);
  // });

  const maxAsIsLtv = resolveMaxAsIsLtv(loanResultData, selectedBuyDownOptions);
  const monthlyQualifyingPayment = resolveMonthlyQualifyingPayment(
    selectedBuyDownOptions,
    loanResultData
  );
  const monthlyQualifyingPI = resolveMonthlyQualifyingPI(
    selectedBuyDownOptions,
    loanResultData
  );
  const requestedLTVAsIs = loanResultData?.loanFeatures?.originalAsIsLtv;
  const requestedLTVARV = resolveRequestedLTVARV(loanResultData);
  const requestedLTC = loanResultData?.loanFeatures?.originalLtc;
  const totalBudget = resolveTotalBudget(newDataToBeReffered, loanResultData);
  const loanPurpose = resolveLoanPurpose(
    newDataToBeReffered,
    loanResultData,
    loanData,
    assignedPartner,
    loanId
  );
  const propertyAddress = resolvePropertyAddress(
    propertyDetails,
    newDataToBeReffered
  );
  const indexOfMostRestrictiveProperty =
    resolveIndexOfMostRestrictiveProperty(loanData);
  const prepaymentPenaltyTypeMonths =
    resolvePrepaymentPenaltyTypeMonths(newDataToBeReffered);
  const prepayPenaltyType = resolvePrepayPenaltyType(
    prepaymentPenaltyTypeMonths,
    newDataToBeReffered
  );

  const currentRateData = calculateCurrentSelectedInterestRate(
    buildDiscountPointsRateInput(currentSelectedInterestRate, selectedBuyDownOptions),
    waiverRequest,
    buyDownOptions
  );
  const discountPoints = currentRateData?.discountPoints;
  const discountPointsWithDollar = currentRateData?.discountPointsWithDollar;

  const estimatedMinimumReserves = resolveEstimatedMinimumReserves(
    selectedBuyDownOptions,
    leverageAndPricingData
  );

  const noteRate = resolveNoteRate(newDataToBeReffered);

  const loanTypeStr = resolveLoanTypeStr(loanType, fetchData, propertyDetails);

  const ctx = {
    isViewOrDownload,
    isMinDataView,
    termSheetData,
    evaluationID,
    propertyAddress,
    requestedLTVAsIs,
    requestedLTVARV,
    requestedLTC,
    maxAsIsLtv,
    newDataToBeReffered,
    selectedBuyDownOptions,
    leverageAndPricingData,
    autoCalculatedLoanResults,
    currentRateData,
    noteRate,
    propertyDetails,
    indexOfMostRestrictiveProperty,
    estimatedMinimumReserves,
    monthlyQualifyingPayment,
    monthlyQualifyingPI,
    prepayPenaltyType,
    loanTypeStr,
    loanPurpose,
    discountPoints,
    discountPointsWithDollar,
    loanDetails,
    totalBudget,
  };

  switch (loanTypeStr) {
    case "Investor DSCR":
      return buildInvestorDscrTermSheet(ctx);
    case "BridgeLoan24":
      return buildBridgeLoan24TermSheet(ctx);
    case "BridgeLoanGroundUp":
      return buildBridgeLoanGroundUpTermSheet(ctx);
    case "BridgeLoanMultifamily":
      return buildBridgeLoanMultifamilyTermSheet(ctx);
    default:
      break;
  }
}

export const getSuggestedLoanTerms = (
  loanType: string,
  loanData: any,
  t: any,
  termSheetAction: string,
  termSheetData?: any
) => {
  const isViewOrDownload = ["view", "download"].includes(termSheetAction);
  const { loanDetails, propertyDetails, fetchData } = loanData;
  const leverageAndPricingData = resolveSuggestedLeverageAndPricingData(loanData);
  const tableBody = leverageAndPricingData?.proceeds?.tableBody;
  const amountRequested = resolveAmountRequested(tableBody);

  const loanTypeStr = resolveLoanTypeStr(loanType, fetchData, propertyDetails);

  const ctx = {
    isViewOrDownload,
    termSheetData,
    loanDetails,
    leverageAndPricingData,
    amountRequested,
  };

  switch (loanTypeStr) {
    case "BridgeLoan24":
      return buildBridgeLoan24SuggestedTerms(ctx);
    case "BridgeLoanGroundUp":
      return buildBridgeLoanGroundUpSuggestedTerms(ctx);
    case "BridgeLoanMultifamily":
      return buildBridgeLoanMultifamilySuggestedTerms(ctx);
    default:
      break;
  }
};

export const formatDateForPDFName = (date: any) => {
  const pad = (num: any) => {
    return String(num).padStart(2, "0");
  }; // Helper function to pad single digits
  const month = pad(date.getMonth() + 1); // Months are zero-based
  const day = pad(date.getDate());
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${month}-${day}-${year}_${hours}-${minutes}-${seconds}`;
};

export function isNotEmptyObject(obj: any) {
  return (
    obj &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj ?? {}).length > 0
  );
}
